import { connect } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { sortBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState, useRef } from 'react';

// Commons
import { convertUnits } from '@commons/utils/conversion';
import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import {
  formatQuantityForDisplay,
  isValidNumberInput,
  parseQuantityInput,
} from '@commons/utils/numberInput';
import { getTheme } from '@commons/utils/theme';
import { translatedUnitCondensed } from '@commons/utils/translateUnit';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_FONTS } from '@commons/Text';

// Utils
import {
  DATAGRID_COMMON_PROPS,
  DATAGRID_SLOT_PROPS,
  getRecipesDatagridColumns,
} from '../utils/getDatagridsConfig';
import { TRANSFER_DETAILS_VIEW } from '@stocks/StocksInventories/Transfers/utils/constants';
import transferUtils from '@stocks/StocksInventories/Transfers/utils';

import {
  DataGridContainer,
  DecrementButton,
  getDatagridStyle,
  GridContainer,
  HorizontalButtonContainer,
  IncrementButton,
  QuantityContainer,
  QuantityDisplay,
  QuantityIndicator,
  QuantityInputInline,
  UnitNameWrapper,
} from './styledComponents';

const RecipesDatagrid = (props) => {
  const {
    recipesToDisplay,
    setRecipesToDisplay,
    transferList,
    transferDetailsView,
    user,
    currency,
    newlyAddedReferenceId,
  } = props;

  const theme = getTheme();
  const dataGridRef = useRef(null);

  const [focusedInput, setFocusedInput] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    brandsSearch: false,
  });

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleValueChange = (recipeId, newQuantity, rawValue = null) => {
    const validQuantity = Math.max(0, newQuantity);
    const quantityToUpdate = transferUtils.getTransferQuantityToUpdate(transferDetailsView);

    const updatedRecipes = recipesToDisplay.map((recipe) => {
      if (recipe.id !== recipeId) {
        return recipe;
      }

      return {
        ...recipe,
        selectedQuantity: validQuantity,
        selectedQuantityRaw: formatQuantityForDisplay(rawValue) || validQuantity.toString(),
        [quantityToUpdate]: validQuantity,
      };
    });

    setRecipesToDisplay(updatedRecipes);
  };

  const handleInputFocus = (recipeId) => {
    setFocusedInput(recipeId);
  };

  const handleInputBlur = () => {
    setFocusedInput(null);
  };

  const renderQuantityCell = (recipe) => {
    const isEmpty = !recipe.selectedQuantity;
    const isFocused = focusedInput === recipe.id;
    const isSelected = recipe.selectedQuantity > 0 || isFocused;
    const isHighlighted = transferUtils.hasDifferentQuantities(recipe, transferDetailsView);

    return (
      <QuantityContainer isSelected={isSelected} onClick={() => handleInputFocus(recipe.id)}>
        <QuantityIndicator />
        {isFocused ? (
          <QuantityInputInline
            min="0"
            step="0.1"
            type="text"
            value={recipe.selectedQuantityRaw || recipe.selectedQuantity}
            autoFocus
            onBlur={handleInputBlur}
            onChange={(event) => {
              const inputValue = event.target.value;

              if (!isValidNumberInput(inputValue)) {
                return;
              }

              const parsedValue = parseQuantityInput(inputValue);

              handleValueChange(recipe.id, parsedValue, inputValue);
            }}
            onKeyDown={(e) => {
              // Prevent losing focus when using arrow keys
              e.stopPropagation();
            }}
          />
        ) : (
          <QuantityDisplay isEmpty={isEmpty} isHighlighted={isHighlighted}>
            {isEmpty ? '—' : recipe.selectedQuantityRaw || recipe.selectedQuantity}
          </QuantityDisplay>
        )}
        <UnitNameWrapper>
          <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {translatedUnitCondensed(convertUnits(recipe.unit))}
          </Text>
        </UnitNameWrapper>
      </QuantityContainer>
    );
  };

  const handleIncrementOrDecrement = (recipeId, quantity) => {
    const newQuantity = quantity < 0 ? 0 : quantity;
    const newRawValue = newQuantity.toString();
    handleValueChange(recipeId, newQuantity, newRawValue);
  };

  const renderIncrementButtons = (recipe) => {
    const isDisabled = [
      TRANSFER_DETAILS_VIEW.SENDER_DISABLED,
      TRANSFER_DETAILS_VIEW.DISABLED,
    ].includes(transferDetailsView);

    return (
      <HorizontalButtonContainer>
        <DecrementButton
          disabled={isDisabled || recipe.selectedQuantity <= 0}
          size="small"
          onClick={() => handleIncrementOrDecrement(recipe.id, recipe.selectedQuantity - 1)}
        >
          <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>−</Text>
        </DecrementButton>
        <IncrementButton
          disabled={isDisabled}
          size="small"
          onClick={() => handleIncrementOrDecrement(recipe.id, recipe.selectedQuantity + 1)}
        >
          <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>+</Text>
        </IncrementButton>
      </HorizontalButtonContainer>
    );
  };

  // Auto-scroll to newly added reference
  useEffect(() => {
    if (!newlyAddedReferenceId) {
      return;
    }

    const sortedRecipesToDisplay = sortBy(recipesToDisplay, 'name');
    const rowIndex = sortedRecipesToDisplay.findIndex(({ id }) => id === newlyAddedReferenceId);

    if (rowIndex !== -1) {
      setTimeout(() => {
        // Calculate the page where the row is
        const targetPage = Math.floor(rowIndex / paginationModel.pageSize);
        if (targetPage !== paginationModel.page) {
          setPaginationModel({ ...paginationModel, page: targetPage });
        }

        // Wait for the pagination to update and then scroll to the row
        setTimeout(() => {
          const scrollContainer = dataGridRef.current?.querySelector(
            '.MuiDataGrid-virtualScroller',
          );

          if (scrollContainer) {
            const rowElements = scrollContainer.querySelectorAll('.MuiDataGrid-row');
            const targetRowInPage = rowIndex % paginationModel.pageSize;
            if (rowElements[targetRowInPage]) {
              rowElements[targetRowInPage].scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }
        }, 100);
      }, 100);
    }
  }, [newlyAddedReferenceId, paginationModel.pageSize, paginationModel.page]);

  // Prevent diagonal scroll on tablet
  useEffect(() => {
    let isScrolling = false;
    let scrollDirection = null;
    let touchStartX = 0;
    let touchStartY = 0;

    // Handle diagonal scroll on tablet
    const handleTouchStart = (e) => {
      const scrollContainer = e.target.closest('.MuiDataGrid-virtualScroller');
      if (!scrollContainer) {
        return;
      }

      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isScrolling = false;
      scrollDirection = null;
    };

    const handleTouchMove = (e) => {
      const scrollContainer = e.target.closest('.MuiDataGrid-virtualScroller');
      if (!scrollContainer) {
        return;
      }

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = Math.abs(touchX - touchStartX);
      const deltaY = Math.abs(touchY - touchStartY);

      // Determine the main scroll direction
      if (!isScrolling && (deltaX > 10 || deltaY > 10)) {
        isScrolling = true;
        scrollDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
      }

      // Prevent scroll in the non-main direction
      if (isScrolling && scrollDirection) {
        if (scrollDirection === 'horizontal' && deltaY > deltaX * 0.3) {
          e.preventDefault();
        } else if (scrollDirection === 'vertical' && deltaX > deltaY * 0.3) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      isScrolling = false;
      scrollDirection = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const columns = getRecipesDatagridColumns({
    renderQuantityCell,
    renderIncrementButtons,
    currency,
    transferList,
    transferDetailsView,
    categoryOptions: [
      ...new Set(
        recipesToDisplay.map(({ category }) => category || i18next.t('GENERAL.NONE_VALUE')),
      ),
    ],
  });

  return (
    <DataGridContainer>
      <GridContainer ref={dataGridRef}>
        <DataGridPro
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          localeText={
            DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid.defaultProps
              .localeText
          }
          pageSizeOptions={[50, 100]}
          paginationModel={paginationModel}
          rows={recipesToDisplay}
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                backgroundColor={theme.colors.greys.lightest}
                icon={'/images/inpulse/shopping.svg'}
                iconWidth="160px"
                label={i18next.t(
                  transferDetailsView === TRANSFER_DETAILS_VIEW.DRAFT
                    ? 'STOCKS.TRANSFERS.RECIPES_DATAGRID_EMPTY_STATE_TITLE'
                    : 'STOCKS.TRANSFERS.DATAGRID_EMPTY_STATE_TITLE',
                )}
              />
            ),
            ...DATAGRID_SLOT_PROPS,
          }}
          sortModel={sortModel}
          sx={getDatagridStyle(theme, newlyAddedReferenceId)}
          /*
           * This prop has a high impact on performances but it's the only way we found
           * to prevent the datagrid from randomly scrolling when clicking on an increment/decrement button
           */
          disableVirtualization
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          {...DATAGRID_COMMON_PROPS}
          pagination
          onPaginationModelChange={handlePaginationModelChange}
          onSortModelChange={handleSortModelChange}
        />
      </GridContainer>
    </DataGridContainer>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps)(RecipesDatagrid);
